import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import io from 'socket.io-client';
import AgoraRTC from 'agora-rtc-sdk-ng';

const DEFAULT_AGENT_UID = 20001;

const AGENT_UI_LANG_KEY = 'agent_ui_lang';
const AGENT_SUPPORTED_LANGUAGES_KEY = 'agent_supported_languages';

// Guest-facing service languages — must match Android kiosk + backend whitelist.
const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'zh', label: '中文' },
];

/** UI strings for agent console (call strip + right panel). */
const I18N = {
  en: {
    endCall: 'End call',
    checkIn: 'Check-in (browser)',
    speaker: 'Speaker',
    guestScreenShare: 'Guest Screen Share',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Reset',
    guestMainLabel: 'Guest / Client / Kiosk',
    you: 'You',
    networkUnstable: 'Network unstable — check connection. Video may recover automatically.',
    inCallWith: 'In call with',
    room: 'Room',
    callHistory: 'Call History',
    cameraMic: 'Camera & Microphone',
    settings: 'Settings',
    camera: 'Camera',
    microphone: 'Microphone',
    inputLevel: 'Input level',
    test: 'Test',
    stop: 'Stop',
    saveAndClose: 'Save & Close',
    close: 'Close',
    save: 'Save',
    backendUrl: 'Backend URL',
    ringtone: 'Ringtone',
    muted: 'Muted',
    enabled: 'Enabled',
    noCallRecords: 'No call records yet',
    historyLoadFailed: 'Could not load call history.',
    historyInvalidResponse: 'Server returned an unexpected format (expected a JSON array).',
    historyEmptyHint: 'Rows are stored per agent id. If your database has calls under another id, this list will be empty.',
    historyRecentAllHint:
      'No rows matched your agent id — showing the latest calls from this server for reference (other agents or older browsers may have created them).',
    inProgress: 'In progress',
    clientKiosk: 'Client / Kiosk',
    time: 'Time',
    duration: 'Duration',
  },
  fr: {
    endCall: 'Raccrocher',
    checkIn: 'Enregistrement (navigateur)',
    speaker: 'Haut-parleur',
    guestScreenShare: 'Partage d’écran invité',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Réinitialiser',
    guestMainLabel: 'Invité / Client / Kiosque',
    you: 'Vous',
    networkUnstable: 'Réseau instable — vérifiez la connexion. La vidéo peut se rétablir.',
    inCallWith: 'En appel avec',
    room: 'Salle',
    callHistory: 'Historique d’appels',
    cameraMic: 'Caméra et microphone',
    settings: 'Paramètres',
    camera: 'Caméra',
    microphone: 'Microphone',
    inputLevel: 'Niveau d’entrée',
    test: 'Tester',
    stop: 'Arrêter',
    saveAndClose: 'Enregistrer et fermer',
    close: 'Fermer',
    save: 'Enregistrer',
    backendUrl: 'URL du serveur',
    ringtone: 'Sonnerie',
    muted: 'Muet',
    enabled: 'Activée',
    noCallRecords: 'Aucun historique',
    inProgress: 'En cours',
    clientKiosk: 'Client / Kiosk',
    time: 'Heure',
    duration: 'Durée',
  },
  es: {
    endCall: 'Colgar',
    checkIn: 'Registro (navegador)',
    speaker: 'Altavoz',
    guestScreenShare: 'Pantalla del invitado',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Restablecer',
    guestMainLabel: 'Invitado / Cliente / Kiosco',
    you: 'Usted',
    networkUnstable: 'Red inestable — revise la conexión. El vídeo puede recuperarse.',
    inCallWith: 'En llamada con',
    room: 'Sala',
    callHistory: 'Historial de llamadas',
    cameraMic: 'Cámara y micrófono',
    settings: 'Ajustes',
    camera: 'Cámara',
    microphone: 'Micrófono',
    inputLevel: 'Nivel de entrada',
    test: 'Probar',
    stop: 'Detener',
    saveAndClose: 'Guardar y cerrar',
    close: 'Cerrar',
    save: 'Guardar',
    backendUrl: 'URL del servidor',
    ringtone: 'Tono',
    muted: 'Silenciado',
    enabled: 'Activado',
    noCallRecords: 'Sin historial',
    inProgress: 'En curso',
    clientKiosk: 'Cliente / Kiosk',
    time: 'Hora',
    duration: 'Duración',
  },
  de: {
    endCall: 'Auflegen',
    checkIn: 'Check-in (Browser)',
    speaker: 'Lautsprecher',
    guestScreenShare: 'Bildschirmfreigabe Gast',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Zurücksetzen',
    guestMainLabel: 'Gast / Client / Kiosk',
    you: 'Sie',
    networkUnstable: 'Netzwerk instabil — Verbindung prüfen. Video kann wiederhergestellt werden.',
    inCallWith: 'Im Gespräch mit',
    room: 'Raum',
    callHistory: 'Anrufliste',
    cameraMic: 'Kamera & Mikrofon',
    settings: 'Einstellungen',
    camera: 'Kamera',
    microphone: 'Mikrofon',
    inputLevel: 'Pegel',
    test: 'Test',
    stop: 'Stopp',
    saveAndClose: 'Speichern & Schließen',
    close: 'Schließen',
    save: 'Speichern',
    backendUrl: 'Backend-URL',
    ringtone: 'Klingelton',
    muted: 'Stumm',
    enabled: 'Ein',
    noCallRecords: 'Keine Einträge',
    inProgress: 'Läuft',
    clientKiosk: 'Client / Kiosk',
    time: 'Zeit',
    duration: 'Dauer',
  },
  it: {
    endCall: 'Riaggancia',
    checkIn: 'Check-in (browser)',
    speaker: 'Altoparlante',
    guestScreenShare: 'Schermo ospite',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Reimposta',
    guestMainLabel: 'Ospite / Client / Kiosk',
    you: 'Tu',
    networkUnstable: 'Rete instabile — controlla la connessione. Il video può ripristinarsi.',
    inCallWith: 'In chiamata con',
    room: 'Stanza',
    callHistory: 'Cronologia chiamate',
    cameraMic: 'Fotocamera e microfono',
    settings: 'Impostazioni',
    camera: 'Fotocamera',
    microphone: 'Microfono',
    inputLevel: 'Livello ingresso',
    test: 'Prova',
    stop: 'Stop',
    saveAndClose: 'Salva e chiudi',
    close: 'Chiudi',
    save: 'Salva',
    backendUrl: 'URL backend',
    ringtone: 'Suoneria',
    muted: 'Silenzioso',
    enabled: 'Attivo',
    noCallRecords: 'Nessuna chiamata',
    inProgress: 'In corso',
    clientKiosk: 'Client / Kiosk',
    time: 'Ora',
    duration: 'Durata',
  },
  nl: {
    endCall: 'Ophangen',
    checkIn: 'Check-in (browser)',
    speaker: 'Luidspreker',
    guestScreenShare: 'Scherm gast',
    zoomIn: 'Zoom +',
    zoomOut: 'Zoom −',
    zoomReset: 'Herstel',
    guestMainLabel: 'Gast / Client / Kiosk',
    you: 'Jij',
    networkUnstable: 'Netwerk onstabiel — controleer verbinding. Video kan herstellen.',
    inCallWith: 'In gesprek met',
    room: 'Kamer',
    callHistory: 'Belgeschiedenis',
    cameraMic: 'Camera en microfoon',
    settings: 'Instellingen',
    camera: 'Camera',
    microphone: 'Microfoon',
    inputLevel: 'Invoerniveau',
    test: 'Test',
    stop: 'Stop',
    saveAndClose: 'Opslaan en sluiten',
    close: 'Sluiten',
    save: 'Opslaan',
    backendUrl: 'Backend-URL',
    ringtone: 'Beltoon',
    muted: 'Dempen',
    enabled: 'Aan',
    noCallRecords: 'Geen records',
    inProgress: 'Bezig',
    clientKiosk: 'Client / Kiosk',
    time: 'Tijd',
    duration: 'Duur',
  },
  zh: {
    endCall: '挂断',
    checkIn: '入住办理（浏览器）',
    speaker: '扬声器',
    guestScreenShare: '客人屏幕共享',
    zoomIn: '放大',
    zoomOut: '缩小',
    zoomReset: '重置',
    guestMainLabel: '客人 / 客户端 / 终端',
    you: '您',
    networkUnstable: '网络不稳定 — 请检查连接，视频可能会自动恢复。',
    inCallWith: '通话中',
    room: '房间',
    callHistory: '通话记录',
    cameraMic: '摄像头与麦克风',
    settings: '设置',
    camera: '摄像头',
    microphone: '麦克风',
    inputLevel: '输入电平',
    test: '测试',
    stop: '停止',
    saveAndClose: '保存并关闭',
    close: '关闭',
    save: '保存',
    backendUrl: '后端地址',
    ringtone: '铃声',
    muted: '静音',
    enabled: '开启',
    noCallRecords: '暂无通话记录',
    inProgress: '进行中',
    clientKiosk: '客户 / 终端',
    time: '时间',
    duration: '时长',
  },
};

function getAgentUiLang() {
  try {
    const saved = localStorage.getItem(AGENT_UI_LANG_KEY);
    if (saved && I18N[saved]) return saved;
  } catch (_) {
    /* ignore */
  }
  const nav = typeof navigator !== 'undefined' && navigator.language ? navigator.language.slice(0, 2).toLowerCase() : 'en';
  return I18N[nav] ? nav : 'en';
}

function tr(lang, key) {
  const pack = I18N[lang] || I18N.en;
  return pack[key] ?? I18N.en[key] ?? key;
}

/** Debug fields for Agora Web / browser MediaStreamTrack (screen vs camera). */
function collectRemoteVideoTrackDebugMeta(track) {
  if (!track) return {};
  let mst = null;
  try {
    mst = typeof track.getMediaStreamTrack === 'function' ? track.getMediaStreamTrack() : null;
  } catch (_) {
    mst = null;
  }
  return {
    trackMediaType: track.trackMediaType,
    videoSourceType: track.videoSourceType,
    sourceType: track.sourceType,
    _$videoSourceType: track._$videoSourceType,
    _videoSourceType: track._videoSourceType,
    mediaStreamContentHint: mst?.contentHint,
    mediaStreamLabel: mst?.label,
    mediaStreamId: mst?.id,
  };
}

/**
 * Classify remote video as camera vs screen share.
 * - Prefer SDK fields (videoSourceType / internal mirrors) when present.
 * - If primary kiosk Agora uid is known and differs from publisher uid → treat as screen (aux uid).
 */
function classifyRemoteVideoStream(track, user, primaryRemoteUid) {
  const meta = collectRemoteVideoTrackDebugMeta(track);
  if (primaryRemoteUid != null && user.uid !== primaryRemoteUid) {
    return { role: 'screen', reason: 'uid_differs_from_clientAgoraUid', meta };
  }
  const vst = track.videoSourceType ?? track._videoSourceType ?? track._$videoSourceType;
  if (vst === 'screen' || vst === 'SCREEN' || vst === 3 || vst === 2) {
    return { role: 'screen', reason: 'track_videoSourceType', meta: { ...meta, rawVideoSourceType: vst } };
  }
  const mst = typeof track.getMediaStreamTrack === 'function' ? track.getMediaStreamTrack() : null;
  if (mst?.contentHint === 'detail') {
    return { role: 'screen', reason: 'mediaTrack_contentHint_detail', meta };
  }
  if (mst?.label) {
    const label = String(mst.label).toLowerCase();
    if (
      label.includes('display')
      || label.includes('screen')
      || label.includes('projection')
      || label.includes('cast')
      || label.includes('screencap')
    ) {
      return { role: 'screen', reason: 'mediaTrack_label', meta: { ...meta, label: mst.label } };
    }
  }
  try {
    if (mst && typeof mst.getSettings === 'function') {
      const st = mst.getSettings();
      if (st && st.displaySurface) {
        return { role: 'screen', reason: 'mediaTrack_getSettings_displaySurface', meta: { ...meta, displaySurface: st.displaySurface } };
      }
    }
  } catch (_) {
    /* ignore */
  }
  return { role: 'camera', reason: 'default_camera', meta };
}

/**
 * Android kiosk often republishes screen capture on the same Agora uid without WebRTC/Agora
 * "screen" markers in the browser, so classifyRemoteVideoStream stays "camera" and the agent
 * never enters state B (Guest Screen Share + zoom).
 * Heuristic: after the first video publish from the kiosk uid, treat further publishes as screen
 * unless dimensions look like classic VGA-ish camera (640×480 or 480×640).
 */
function inferSameUidSecondaryPublishAsScreen(track, user, primaryRemoteUid, publishIndex) {
  if (primaryRemoteUid == null || String(user.uid) !== String(primaryRemoteUid)) return false;
  if (publishIndex < 2) return false;
  let w = 0;
  let h = 0;
  try {
    const mst = typeof track.getMediaStreamTrack === 'function' ? track.getMediaStreamTrack() : null;
    const st = mst?.getSettings?.();
    w = Number(st?.width) || 0;
    h = Number(st?.height) || 0;
  } catch (_) {
    /* ignore */
  }
  const vgaLikish = (w === 640 && h === 480) || (w === 480 && h === 640);
  if (vgaLikish) return false;
  return true;
}

/** Agora track.play() may return a Promise; swallow expected AbortError from overlapping loads. */
function guardAgoraPlayResult(result, context) {
  if (result && typeof result.catch === 'function') {
    result.catch((err) => {
      if (err && err.name === 'AbortError') return;
      console.warn('[agora] play rejected', context, err);
    });
  }
}

/** Detach remote camera from any DOM (e.g. wrong screen host), then play on main guest container. */
function stopRemoteVideoTrackPlayback(track, context) {
  if (!track || typeof track.stop !== 'function') return;
  try {
    track.stop();
  } catch (e) {
    console.warn('[agora] remote video track.stop', context, e);
  }
}

const getDefaultBackendUrl = () => {
  try {
    const saved = localStorage.getItem('agent_backend_url');
    if (saved) return saved;
  } catch (_) {
    /* ignore */
  }
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  return 'https://hotelkiosk.top';
};

const AGENT_ID_OVERRIDE_KEY = 'agent_id_override';

/**
 * Stable agent id for call_logs / queue (no random per browser).
 * Priority: localStorage override → REACT_APP_AGENT_ID (build-time) → default.
 * Console: localStorage.setItem('agent_id_override','agent_e9h7mr9ydl'); location.reload()
 */
function loadAgentId() {
  try {
    const saved = localStorage.getItem(AGENT_ID_OVERRIDE_KEY);
    if (saved && String(saved).trim()) return String(saved).trim();
  } catch {
    /* ignore */
  }
  const fromEnv = process.env.REACT_APP_AGENT_ID;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  return 'frontdesk_01';
}

function AgentDashboard() {
  const [agentId] = useState(loadAgentId);
  const [backendUrl, setBackendUrl] = useState(getDefaultBackendUrl());
  const [editingBackendUrl, setEditingBackendUrl] = useState(getDefaultBackendUrl());
  const [queueLength, setQueueLength] = useState(0);
  const [inCall, setInCall] = useState(false);
  const [remoteUserId, setRemoteUserId] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoadError, setHistoryLoadError] = useState('');
  /** 'agent' | 'recent_fallback' | 'none' — from API after successful load */
  const [historySource, setHistorySource] = useState('none');
  const [activeView, setActiveView] = useState('queue');
  const [cameras, setCameras] = useState([]);
  const [microphones, setMicrophones] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [previewStream, setPreviewStream] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [micVolume, setMicVolume] = useState(0);
  const [ringtoneMuted, setRingtoneMuted] = useState(() => localStorage.getItem('agent_ringtone_muted') === '1');
  const [ringtoneVolume, setRingtoneVolume] = useState(() => {
    const raw = Number(localStorage.getItem('agent_ringtone_volume') || '80');
    return Number.isNaN(raw) ? 80 : Math.max(0, Math.min(100, raw));
  });
  const [isRinging, setIsRinging] = useState(false);
  const [pendingCall, setPendingCall] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [supportedLanguages, setSupportedLanguages] = useState(() => {
    try {
      const raw = localStorage.getItem(AGENT_SUPPORTED_LANGUAGES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((code) => SUPPORTED_LANGUAGES.some((s) => s.code === code));
    } catch {
      return [];
    }
  });
  const [agoraNetworkWarn, setAgoraNetworkWarn] = useState(false);
  const [screenShareActive, setScreenShareActive] = useState(false);
  /** Bumps after DOM can show the screen-share column (was display:none → Agora play needs layout pass). */
  const [screenShareLayoutEpoch, setScreenShareLayoutEpoch] = useState(0);
  const [screenShareScale, setScreenShareScale] = useState(1);
  const [screenShareOffsetX, setScreenShareOffsetX] = useState(0);
  const [screenShareOffsetY, setScreenShareOffsetY] = useState(0);
  const [isDraggingScreenShare, setIsDraggingScreenShare] = useState(false);
  /** Default agent UI locale from storage; wired for future selector (copy currently uses `tr('en', …)`). */
  const agentDefaultUiLang = useMemo(() => getAgentUiLang(), []);
  const volumeIntervalRef = useRef(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const screenShareVideoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const socketRef = useRef(null);
  const languageSyncTimeoutIdsRef = useRef([]);
  const languageSyncIntervalRef = useRef(null);
  const agoraAppIdRef = useRef('');
  const clientRef = useRef(null);
  const localTracksRef = useRef(null);
  const audioCtxRef = useRef(null);
  const ringIntervalRef = useRef(null);
  const queueLengthRef = useRef(0);
  const selectedCameraRef = useRef('');
  const selectedMicRef = useRef('');
  const callSessionRef = useRef(0);
  const inCallRef = useRef(false);
  const isConnectingRef = useRef(false);
  const pendingCallRef = useRef(null);
  const pendingOfferQueueRef = useRef([]);
  const showNextPendingOfferRef = useRef(() => {});
  const ringtoneMutedRef = useRef(ringtoneMuted);
  /** Expected kiosk / guest Agora uid (from server); fallback: first user-joined remote. */
  const primaryRemoteUidRef = useRef(null);
  const remoteCameraSlotRef = useRef({ track: null, uid: null });
  const remoteScreenSlotRef = useRef({ track: null, uid: null });
  const remoteVideoPublishCountRef = useRef({});
  const screenShareOffsetDragRef = useRef({ x: 0, y: 0 });
  const screenShareDragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  useEffect(() => {
    screenShareOffsetDragRef.current = { x: screenShareOffsetX, y: screenShareOffsetY };
  }, [screenShareOffsetX, screenShareOffsetY]);

  useEffect(() => {
    if (screenShareActive) return;
    setScreenShareScale(1);
    setScreenShareOffsetX(0);
    setScreenShareOffsetY(0);
    setIsDraggingScreenShare(false);
    screenShareDragRef.current.active = false;
  }, [screenShareActive]);

  useEffect(() => {
    inCallRef.current = inCall;
  }, [inCall]);

  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  useEffect(() => {
    pendingCallRef.current = pendingCall;
  }, [pendingCall]);

  /** Warn before refresh/close while a call is active, incoming, or connecting (browser-native prompt). */
  useEffect(() => {
    const shouldBlock = inCall || !!pendingCall || isConnecting;
    if (!shouldBlock) return;

    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [inCall, pendingCall, isConnecting]);

  useEffect(() => {
    if (!screenShareActive || screenShareScale > 1) return;
    setScreenShareOffsetX(0);
    setScreenShareOffsetY(0);
  }, [screenShareActive, screenShareScale]);

  const handleScreenShareZoomIn = useCallback(() => {
    if (!screenShareActive || !remoteScreenSlotRef.current?.track) return;
    setScreenShareScale((s) => Math.min(2, Math.round((s + 0.15) * 1000) / 1000));
  }, [screenShareActive]);

  const handleScreenShareZoomOut = useCallback(() => {
    if (!screenShareActive || !remoteScreenSlotRef.current?.track) return;
    setScreenShareScale((s) => Math.max(1, Math.round((s - 0.15) * 1000) / 1000));
  }, [screenShareActive]);

  const handleScreenShareZoomReset = useCallback(() => {
    setScreenShareScale(1);
    setScreenShareOffsetX(0);
    setScreenShareOffsetY(0);
  }, []);

  const handleScreenShareViewportPointerDown = useCallback((e) => {
    if (!screenShareActive || !remoteScreenSlotRef.current?.track || screenShareScale <= 1) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const t = e.target;
    if (t && typeof t.closest === 'function' && t.closest('button')) return;
    const d = screenShareDragRef.current;
    d.active = true;
    d.pointerId = e.pointerId;
    d.startX = e.clientX;
    d.startY = e.clientY;
    const origin = screenShareOffsetDragRef.current;
    d.originX = origin.x;
    d.originY = origin.y;
    setIsDraggingScreenShare(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) { /* ignore */ }
  }, [screenShareActive, screenShareScale]);

  const handleScreenShareViewportPointerMove = useCallback((e) => {
    const d = screenShareDragRef.current;
    if (!d.active || e.pointerId !== d.pointerId) return;
    setScreenShareOffsetX(d.originX + (e.clientX - d.startX));
    setScreenShareOffsetY(d.originY + (e.clientY - d.startY));
  }, []);

  const handleScreenShareViewportPointerUp = useCallback((e) => {
    const d = screenShareDragRef.current;
    if (!d.active || e.pointerId !== d.pointerId) return;
    d.active = false;
    d.pointerId = null;
    setIsDraggingScreenShare(false);
    try {
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch (_) { /* ignore */ }
  }, []);

  useLayoutEffect(() => {
    if (!screenShareActive || !remoteScreenSlotRef.current?.track) return;
    const el = screenShareVideoRef.current;
    const track = remoteScreenSlotRef.current?.track;
    const camTrack = remoteCameraSlotRef.current?.track;
    if (!el || !track) return;
    if (camTrack && track === camTrack) {
      console.warn('[agora] skip guest-screen play: screen slot track is same ref as camera (mis-bind risk)');
      return;
    }
    try {
      guardAgoraPlayResult(track.play(el), 'guest-screen');
    } catch (e) {
      console.warn('[agora] guest screen play after layout failed', e);
    }
    const retry = window.setTimeout(() => {
      const el2 = screenShareVideoRef.current;
      const tr2 = remoteScreenSlotRef.current?.track;
      const cam2 = remoteCameraSlotRef.current?.track;
      if (!el2 || !tr2 || tr2 !== track) return;
      if (cam2 && tr2 === cam2) return;
      try {
        guardAgoraPlayResult(tr2.play(el2), 'guest-screen-retry');
      } catch (e2) {
        console.warn('[agora] guest screen play retry failed', e2);
      }
    }, 220);
    return () => window.clearTimeout(retry);
  }, [screenShareActive, screenShareLayoutEpoch]);

  useEffect(() => {
    ringtoneMutedRef.current = ringtoneMuted;
  }, [ringtoneMuted]);

  // Load device list without requesting permissions yet
  const loadDevices = async () => {
    try {
      const devices = await AgoraRTC.getDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const audioDevices = devices.filter(d => d.kind === 'audioinput');
      setCameras(videoDevices);
      setMicrophones(audioDevices);
      if (!selectedCamera && videoDevices.length) {
        setSelectedCamera(videoDevices[0].deviceId);
      }
      if (!selectedMic && audioDevices.length) {
        setSelectedMic(audioDevices[0].deviceId);
      }
    } catch (err) {
      console.error('Failed to get devices', err);
    }
  };

  // Start preview with volume monitoring
  const startPreview = async () => {
    if (previewStream) {
      previewStream.audioTrack?.close();
      previewStream.videoTrack?.close();
      setPreviewStream(null);
    }
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      setMicVolume(0);
    }
    setPreviewError('');
    try {
      // Force request permissions
      const testTracks = await AgoraRTC.createMicrophoneAndCameraTracks();
      testTracks.forEach(track => track.close());

      const devices = await AgoraRTC.getDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const audioDevices = devices.filter(d => d.kind === 'audioinput');
      setCameras(videoDevices);
      setMicrophones(audioDevices);

      const camId = selectedCamera || (videoDevices[0]?.deviceId);
      const micId = selectedMic || (audioDevices[0]?.deviceId);
      if (!camId || !micId) {
        throw new Error('No camera or microphone found');
      }

      const tracks = await AgoraRTC.createMicrophoneAndCameraTracks(micId, camId);
      const [audioTrack, videoTrack] = tracks;
      setPreviewStream({ audioTrack, videoTrack });
      if (previewVideoRef.current) {
        videoTrack.play(previewVideoRef.current);
      }

      // Start volume monitoring
      volumeIntervalRef.current = setInterval(() => {
        if (audioTrack && typeof audioTrack.getVolumeLevel === 'function') {
          const level = audioTrack.getVolumeLevel();
          const percent = Math.min(100, Math.floor(level * 100));
          setMicVolume(percent);
        } else {
          setMicVolume(prev => (Math.random() * 30));
        }
      }, 100);
    } catch (err) {
      console.error(err);
      setPreviewError('Unable to access camera/mic. Please check permissions and ensure no other app is using them.');
    }
  };

  const stopPreview = () => {
    if (previewStream) {
      previewStream.audioTrack?.close();
      previewStream.videoTrack?.close();
      setPreviewStream(null);
    }
    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
      setMicVolume(0);
    }
  };

  const saveDeviceSettings = () => {
    stopPreview();
    setActiveView('queue');
    localStorage.setItem('agent_selected_camera', selectedCamera);
    localStorage.setItem('agent_selected_mic', selectedMic);
  };

  useEffect(() => {
    const savedCam = localStorage.getItem('agent_selected_camera');
    const savedMic = localStorage.getItem('agent_selected_mic');
    if (savedCam) setSelectedCamera(savedCam);
    if (savedMic) setSelectedMic(savedMic);
    loadDevices();
  }, []);

  useEffect(() => {
    queueLengthRef.current = queueLength;
  }, [queueLength]);

  useEffect(() => {
    selectedCameraRef.current = selectedCamera;
  }, [selectedCamera]);

  useEffect(() => {
    selectedMicRef.current = selectedMic;
  }, [selectedMic]);

  useEffect(() => {
    const handleDeviceChange = () => loadDevices();
    AgoraRTC.on('camera-changed', handleDeviceChange);
    AgoraRTC.on('microphone-changed', handleDeviceChange);
    return () => {
      AgoraRTC.off('camera-changed', handleDeviceChange);
      AgoraRTC.off('microphone-changed', handleDeviceChange);
    };
  }, []);

  /** Bind local preview once per call when inCall turns on (not on screen-share layout toggles). */
  useEffect(() => {
    if (!inCall) return;

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const videoTrack = localTracksRef.current?.[1];
        if (videoTrack && localVideoRef.current) {
          try {
            console.log('Playing local video after render:', localVideoRef.current);
            guardAgoraPlayResult(videoTrack.play(localVideoRef.current), 'local-after-inCall');
          } catch (err) {
            console.warn('Failed to play local video after render:', err);
          }
        } else {
          console.warn('Local video not ready after render', {
            videoTrack: !!videoTrack,
            localVideoRef: !!localVideoRef.current,
          });
        }
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [inCall]);

  /** Re-attach remote camera to container once per call if track already exists (DOM is stable across screen share). */
  useLayoutEffect(() => {
    if (!inCall) return;
    const track = remoteCameraSlotRef.current?.track;
    const el = remoteVideoRef.current;
    if (!track || !el) return;
    try {
      guardAgoraPlayResult(track.play(el), 'remote-camera-inCall-layout');
    } catch (e) {
      console.warn('[agora] remote camera play after layout', e);
    }
  }, [inCall]);

  const loadHistory = useCallback(async () => {
    setHistoryLoadError('');
    setHistorySource('none');
    try {
      const url = `${backendUrl.replace(/\/+$/, '')}/api/agent/history/${encodeURIComponent(agentId)}`;
      const res = await fetch(url);
      if (!res.ok) {
        let detail = '';
        try {
          const errBody = await res.json();
          detail = errBody?.error ? ` ${String(errBody.error)}` : '';
        } catch (_) {
          /* ignore */
        }
        throw new Error(`HTTP ${res.status}${detail}`);
      }
      const data = await res.json();
      let items = [];
      let src = 'agent';
      if (Array.isArray(data)) {
        items = data;
        src = 'agent';
      } else if (data && Array.isArray(data.items)) {
        items = data.items;
        src = data.historySource === 'recent_fallback' || data.historySource === 'agent' || data.historySource === 'none'
          ? data.historySource
          : 'agent';
      } else {
        console.warn('loadHistory: unexpected payload', data);
        setHistory([]);
        setHistorySource('none');
        setHistoryLoadError(tr('en', 'historyInvalidResponse'));
        return;
      }
      setHistory(items);
      setHistorySource(src);
    } catch (error) {
      console.warn('loadHistory failed', error);
      setHistory([]);
      setHistorySource('none');
      setHistoryLoadError(`${tr('en', 'historyLoadFailed')} ${error?.message || ''}`.trim());
    }
  }, [agentId, backendUrl]);

  /** Force kiosk camera onto remoteVideoRef; stop() first so Agora detaches from screenShareVideoRef if mis-bound. */
  const replayRemoteCameraToRemoteVideoHost = useCallback((context) => {
    const cameraTrack = remoteCameraSlotRef.current?.track;
    const rv = remoteVideoRef.current;
    try {
      const screenHost = screenShareVideoRef.current;
      if (screenHost) screenHost.innerHTML = '';
    } catch (e) {
      console.warn('[agora] clear screen-share host DOM', context, e);
    }
    stopRemoteVideoTrackPlayback(cameraTrack, `${context}-preplay`);
    if (!cameraTrack || !rv) return;
    try {
      rv.innerHTML = '';
      guardAgoraPlayResult(cameraTrack.play(rv), context);
    } catch (e) {
      console.warn('[agora] replay remote camera to main host failed', context, e);
    }
  }, []);

  /** Screen-share UI cleanup only (does not end the call). */
  const clearScreenShareLayout = useCallback((meta = {}) => {
    const slot = remoteScreenSlotRef.current;
    if (slot?.track) {
      try {
        slot.track.stop();
      } catch (e) {
        console.warn('[agora] clearScreenShareLayout: screen track stop', e);
      }
    }
    remoteScreenSlotRef.current = { track: null, uid: null };
    setScreenShareActive(false);
    setScreenShareLayoutEpoch(0);
    setScreenShareScale(1);
    setScreenShareOffsetX(0);
    setScreenShareOffsetY(0);
    setIsDraggingScreenShare(false);
    screenShareDragRef.current.active = false;
    try {
      const host = screenShareVideoRef.current;
      if (host) host.innerHTML = '';
    } catch (e) {
      console.warn('[agora] clearScreenShareLayout: clear screen DOM', e);
    }
    replayRemoteCameraToRemoteVideoHost('clearScreenShareLayout');
    if (meta && meta.reason) {
      console.log('[agora] clearScreenShareLayout', meta);
    }
  }, [replayRemoteCameraToRemoteVideoHost]);

  useLayoutEffect(() => {
    if (screenShareActive && remoteScreenSlotRef.current?.track) return;
    replayRemoteCameraToRemoteVideoHost('screen-share-inactive-layout');
  }, [screenShareActive, screenShareLayoutEpoch, replayRemoteCameraToRemoteVideoHost]);

  /** UI safety: if share flag is on but screen slot is empty, drop flag so layout returns to state A. */
  useLayoutEffect(() => {
    if (!screenShareActive) return;
    if (remoteScreenSlotRef.current?.track) return;
    setScreenShareActive(false);
  }, [screenShareActive, screenShareLayoutEpoch]);

  const rightPanelView = activeView === 'devices' || activeView === 'settings' ? activeView : 'history';

  useEffect(() => {
    if (rightPanelView !== 'history') return;
    loadHistory();
  }, [rightPanelView, loadHistory]);

  const ensureAudioContext = async () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      try {
        await audioCtxRef.current.resume();
      } catch (err) {
        console.warn('Failed to resume audio context', err);
      }
    }
    return audioCtxRef.current;
  };

  const playBeep = async (frequency = 880, durationMs = 140) => {
    if (ringtoneMutedRef.current) return;
    const ctx = await ensureAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        console.warn('AudioContext resume blocked until user gesture', err);
        return;
      }
    }
    const normalizedVolume = Math.max(0, Math.min(100, ringtoneVolume)) / 100;
    const peakGain = 0.02 + normalizedVolume * 0.22;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(peakGain, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.01);
  };

  const stopRinging = () => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    setIsRinging(false);
  };

  const startRinging = (type = 'incoming') => {
    stopRinging();
    setIsRinging(true);
    const pattern = type === 'outgoing'
      ? { tones: [[740, 120], [740, 120]], spacing: 900 }
      : type === 'queue'
        ? { tones: [[960, 120], [960, 120]], spacing: 1100 }
        : { tones: [[660, 150], [830, 150]], spacing: 1000 };
    const runPattern = () => {
      pattern.tones.forEach(([freq, dur], idx) => {
        setTimeout(() => playBeep(freq, dur), idx * 180);
      });
    };
    runPattern();
    ringIntervalRef.current = setInterval(runPattern, pattern.spacing);
  };

  const showNextPendingOffer = useCallback(() => {
    if (inCallRef.current) return;
    if (isConnectingRef.current) return;
    if (pendingCallRef.current) return;
    const next = pendingOfferQueueRef.current.shift();
    if (!next) return;
    console.log('[offer-queue] show next pending', {
      userId: next.userId,
      roomId: next.roomId,
    });
    ensureAudioContext().then((ctx) => {
      if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
    }).finally(() => {
      startRinging('incoming');
    });
    setPendingCall(next);
  }, []);

  useEffect(() => {
    showNextPendingOfferRef.current = showNextPendingOffer;
  }, [showNextPendingOffer]);

  const fetchAgoraAppId = useCallback(async () => {
    const base = backendUrl.replace(/\/+$/, '');
    if (!base) throw new Error('Backend URL is not configured');
    const res = await fetch(`${base}/api/config/agora`);
    if (!res.ok) {
      throw new Error(`Failed to load Agora config (HTTP ${res.status})`);
    }
    const data = await res.json();
    const appId = (data?.appId || '').trim();
    if (!appId) throw new Error('Server returned an empty Agora App ID');
    agoraAppIdRef.current = appId;
    return appId;
  }, [backendUrl]);

  useEffect(() => {
    fetchAgoraAppId().catch((err) => {
      console.warn('[config] Agora App ID load failed:', err?.message || err);
      agoraAppIdRef.current = '';
    });
  }, [fetchAgoraAppId]);

  const emitAgentLanguages = useCallback((languages) => {
    socketRef.current?.emit('agent_languages_update', { agentId, languages });
  }, [agentId]);

  const handleSupportedLanguageToggle = useCallback((code) => {
    setSupportedLanguages((prev) => {
      const next = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
      try {
        localStorage.setItem(AGENT_SUPPORTED_LANGUAGES_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      emitAgentLanguages(next);
      return next;
    });
  }, [emitAgentLanguages]);

  useEffect(() => {
    const socketBase = backendUrl.replace(/\/+$/, '');
    const socket = io(socketBase, {
      /** Polling first so a flaky WS path still connects; Engine.IO may upgrade to websocket. */
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socketRef.current = socket;
    ensureAudioContext();

    const clearLanguageSyncTimeouts = () => {
      languageSyncTimeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
      languageSyncTimeoutIdsRef.current = [];
    };

    const stopLanguageHeartbeat = () => {
      clearLanguageSyncTimeouts();
      if (languageSyncIntervalRef.current != null) {
        window.clearInterval(languageSyncIntervalRef.current);
        languageSyncIntervalRef.current = null;
      }
    };

    const emitSavedAgentLanguages = () => {
      const activeSocket = socketRef.current;
      if (!activeSocket?.connected) return;
      try {
        const raw = localStorage.getItem(AGENT_SUPPORTED_LANGUAGES_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        const languages = Array.isArray(parsed)
          ? parsed.filter((c) => SUPPORTED_LANGUAGES.some((s) => s.code === c))
          : [];
        activeSocket.emit('agent_languages_update', { agentId, languages });
      } catch {
        activeSocket.emit('agent_languages_update', { agentId, languages: [] });
      }
    };

    const syncAgentLanguagesIfConnected = () => {
      if (!socketRef.current?.connected) return;
      emitSavedAgentLanguages();
    };

    const scheduleLanguageSyncRetries = () => {
      const id300 = window.setTimeout(() => syncAgentLanguagesIfConnected(), 300);
      const id1000 = window.setTimeout(() => syncAgentLanguagesIfConnected(), 1000);
      languageSyncTimeoutIdsRef.current.push(id300, id1000);
    };

    const startLanguageHeartbeat = () => {
      stopLanguageHeartbeat();
      languageSyncIntervalRef.current = window.setInterval(() => {
        syncAgentLanguagesIfConnected();
      }, 10_000);
    };

    const loginAgent = () => {
      socket.emit('agent_login', { agentId });
    };

    socket.on('agent_logged_in', (payload) => {
      console.log('[socket] agent_logged_in', payload);
      syncAgentLanguagesIfConnected();
      if (!inCallRef.current) {
        socket.emit('agent_status', { agentId, status: 'idle' });
      }
      scheduleLanguageSyncRetries();
      startLanguageHeartbeat();
    });

    socket.on('connect', () => {
      setConnectionStatus('connected');
      loginAgent();
      syncAgentLanguagesIfConnected();
      ensureAudioContext().then((ctx) => {
        if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      });
    });

    socket.on('disconnect', () => {
      stopLanguageHeartbeat();
      setConnectionStatus('disconnected');
    });

    const onWindowFocus = () => syncAgentLanguagesIfConnected();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncAgentLanguagesIfConnected();
      }
    };
    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    socket.on('connect_error', () => {
      setConnectionStatus('error');
    });

    socket.on('queue_list_update', (data) => {
      const prevLen = queueLengthRef.current;
      const newLen = Number(data?.queueLength ?? 0);
      if (!ringtoneMutedRef.current && newLen > prevLen) {
        startRinging('queue');
      }
      setQueueLength(Number.isNaN(newLen) ? 0 : newLen);
    });

    socket.on('call_offer', ({ roomId, token, userId, uid, clientAgoraUid }) => {
      const offer = { roomId, token, userId, uid, clientAgoraUid };
      console.log('[socket] call_offer', offer);

      if (inCallRef.current || isConnectingRef.current) {
        pendingOfferQueueRef.current.push(offer);
        console.log('[offer-queue] queued while inCall', {
          userId,
          roomId,
          queueDepth: pendingOfferQueueRef.current.length,
        });
        return;
      }

      if (pendingCallRef.current) {
        pendingOfferQueueRef.current.push(offer);
        console.log('[offer-queue] queued behind pending', {
          userId,
          roomId,
          queueDepth: pendingOfferQueueRef.current.length,
        });
        return;
      }

      ensureAudioContext().then((ctx) => {
        if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      }).finally(() => {
        startRinging('incoming');
      });
      setPendingCall(offer);
    });

    socket.on('call_start', ({ roomId, token, userId, uid, clientAgoraUid }) => {
      console.log('[socket] call_start received', {
        roomId,
        token,
        userId,
        uid,
        clientAgoraUid,
      });
      stopRinging();
      const agoraUid = uid != null ? uid : DEFAULT_AGENT_UID;
      console.log('[socket] invoking startCall');
      startCall(roomId, token, userId, agoraUid, clientAgoraUid).catch((err) => {
        console.error('startCall from call_start failed', err);
      });
    });

    socket.on('call_ended', async (payload) => {
      const reason = payload?.reason;
      const endedRoomId = payload?.roomId;
      await endCall(false, { clearPending: false });
      if (endedRoomId) {
        setPendingCall((prev) => (prev?.roomId === endedRoomId ? null : prev));
      }
      loadHistory();
      if (reason === 'timeout') {
        alert('Call timed out (30 minute limit).');
      }
      window.setTimeout(() => showNextPendingOfferRef.current(), 100);
      window.setTimeout(() => showNextPendingOfferRef.current(), 300);
    });

    socket.on('health_check', ({ roomId }) => {
      socket.emit('health_ack', { roomId });
    });

    if (socket.connected) {
      setConnectionStatus('connected');
      loginAgent();
    }

    loadHistory();

    return () => {
      stopLanguageHeartbeat();
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      socket.disconnect();
      socketRef.current = null;
      stopRinging();
      stopPreview();
    };
  }, [agentId, backendUrl, loadHistory]);

  useEffect(() => {
    localStorage.setItem('agent_ringtone_muted', ringtoneMuted ? '1' : '0');
  }, [ringtoneMuted]);

  useEffect(() => {
    localStorage.setItem('agent_ringtone_volume', String(ringtoneVolume));
  }, [ringtoneVolume]);

  useEffect(() => {
    const unlock = () => {
      ensureAudioContext().then((ctx) => {
        if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      });
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const saveBackendUrl = () => {
    const nextUrl = editingBackendUrl.trim().replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(nextUrl)) {
      alert('Backend URL must start with http:// or https://');
      return;
    }
    localStorage.setItem('agent_backend_url', nextUrl);
    setBackendUrl(nextUrl);
    setActiveView('queue');
  };

  const leaveClientSafely = async (client) => {
    if (!client) return;
    try {
      await client.leave();
    } catch (leaveErr) {
      console.warn('leave() failed:', leaveErr);
    }
  };

  const startCall = async (roomId, token, userId, agoraUid = DEFAULT_AGENT_UID, clientAgoraUid = null) => {
    console.log('[startCall] begin', {
      roomId,
      token,
      userId,
      uid: agoraUid,
      clientAgoraUid,
    });
    const sessionId = Date.now();
    callSessionRef.current = sessionId;
    primaryRemoteUidRef.current = clientAgoraUid != null ? clientAgoraUid : null;
    remoteCameraSlotRef.current = { track: null, uid: null };
    remoteScreenSlotRef.current = { track: null, uid: null };
    remoteVideoPublishCountRef.current = {};
    setIsConnecting(true);
    try {
      const oldClient = clientRef.current;
      clientRef.current = null;
      await leaveClientSafely(oldClient);

      setInCall(true);
      setCurrentRoomId(roomId);
      setRemoteUserId(userId || null);
      stopRinging();

      stopPreview();

      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;
      setAgoraNetworkWarn(false);

      client.on('connection-state-change', (curState, prevState, reason) => {
        console.log('Agora connection-state-change', curState, prevState, reason);
        if (curState === 'CONNECTED') {
          setAgoraNetworkWarn(false);
        } else if (curState === 'RECONNECTING' || curState === 'DISCONNECTED' || curState === 'DISCONNECTING') {
          setAgoraNetworkWarn(true);
        }
      });

      client.on('user-unpublished', async (user, mediaType) => {
        const isCurrentSession = callSessionRef.current === sessionId && clientRef.current === client;
        if (!isCurrentSession) return;
        console.log('[agora] user-unpublished', { uid: user.uid, mediaType });
        if (mediaType !== 'video') return;

        await Promise.resolve();

        const screenSlot = remoteScreenSlotRef.current;
        const uidMatchesScreen = screenSlot?.uid === user.uid;
        const screenTrackGone =
          !!screenSlot?.track
          && uidMatchesScreen
          && (!user.videoTrack || user.videoTrack !== screenSlot.track);
        if (screenTrackGone) {
          clearScreenShareLayout({
            reason: 'user-unpublished-video',
            uid: user.uid,
            hadVideoTrack: !!user.videoTrack,
          });
          return;
        }

        const camSlot = remoteCameraSlotRef.current;
        if (camSlot.track && camSlot.uid === user.uid) {
          const stillSameCam = user.videoTrack && user.videoTrack === camSlot.track;
          if (!stillSameCam) {
            try {
              camSlot.track.stop();
            } catch (e) {
              console.warn('[agora] camera track stop', e);
            }
            remoteCameraSlotRef.current = { track: null, uid: null };
          }
        }
      });

      client.on('user-left', (user, reason) => {
        console.log('Agora user-left', user?.uid, reason);
        if (callSessionRef.current !== sessionId || clientRef.current !== client) return;
        if (!inCallRef.current) return;
        alert('Customer left the call.');
        endCall(false);
      });

      client.on('user-published', async (user, mediaType) => {
        const isCurrentSession = callSessionRef.current === sessionId && clientRef.current === client;
        if (!isCurrentSession) return;

        console.log('[agora] user-published', { uid: user.uid, mediaType });

        if (mediaType === 'audio') {
          await client.subscribe(user, mediaType);
          if (user.audioTrack) {
            try {
              user.audioTrack.play();
            } catch (err) {
              console.warn('Remote audio autoplay was blocked, tap the page once then retry.', err);
            }
          }
          stopRinging();
          return;
        }

        if (mediaType === 'video') {
          let videoTrack;
          try {
            videoTrack = await client.subscribe(user, 'video');
          } catch (err) {
            console.warn('[agora] subscribe video failed', err);
            return;
          }

          const dbg = collectRemoteVideoTrackDebugMeta(videoTrack);
          const uidKey = String(user.uid);
          const n = (remoteVideoPublishCountRef.current[uidKey] || 0) + 1;
          remoteVideoPublishCountRef.current[uidKey] = n;

          let { role, reason, meta } = classifyRemoteVideoStream(
            videoTrack,
            user,
            primaryRemoteUidRef.current,
          );

          if (role === 'camera' && inferSameUidSecondaryPublishAsScreen(
            videoTrack,
            user,
            primaryRemoteUidRef.current,
            n,
          )) {
            role = 'screen';
            reason = `same_uid_secondary_publish_heuristic_was:${reason}`;
          }

          console.log('[agora] user-published video detail', {
            uid: user.uid,
            mediaType,
            role,
            classifyReason: reason,
            publishIndex: n,
            ...dbg,
            meta,
          });

          if (role === 'screen') {
            /** Single RemoteVideoTrack may be promoted from camera → screen; clear camera slot so refs are not duplicated. */
            if (remoteCameraSlotRef.current.track === videoTrack) {
              remoteCameraSlotRef.current = { track: null, uid: null };
            }
            const prev = remoteScreenSlotRef.current.track;
            if (prev && prev !== videoTrack) {
              try {
                prev.stop();
              } catch (e) {
                console.warn('[agora] stop previous screen track', e);
              }
            }
            remoteScreenSlotRef.current = { track: videoTrack, uid: user.uid };
            setScreenShareActive(true);
            setScreenShareLayoutEpoch((e) => e + 1);
            console.log('[agora] screen share subscribed; play deferred to layout', { uid: user.uid });
          } else {
            /** Same uid republished camera after screen share: clear screen slot even if user-unpublished order is flaky (state C → A). */
            if (remoteScreenSlotRef.current.track && remoteScreenSlotRef.current.uid === user.uid) {
              try {
                remoteScreenSlotRef.current.track.stop();
              } catch (e) {
                console.warn('[agora] stop screen slot on camera publish same uid', e);
              }
              remoteScreenSlotRef.current = { track: null, uid: null };
              setScreenShareActive(false);
              setScreenShareLayoutEpoch((e) => e + 1);
              setScreenShareScale(1);
              setScreenShareOffsetX(0);
              setScreenShareOffsetY(0);
              setIsDraggingScreenShare(false);
              try {
                const host = screenShareVideoRef.current;
                if (host) host.innerHTML = '';
              } catch (_) { /* ignore */ }
            }
            const prev = remoteCameraSlotRef.current.track;
            if (prev && prev !== videoTrack) {
              try {
                prev.stop();
              } catch (e) {
                console.warn('[agora] stop previous camera track', e);
              }
            }
            remoteCameraSlotRef.current = { track: videoTrack, uid: user.uid };
            if (remoteVideoRef.current) {
              try {
                guardAgoraPlayResult(
                  videoTrack.play(remoteVideoRef.current),
                  'remote-user-published',
                );
              } catch (err) {
                console.warn('[agora] remote camera play (user-published) sync error', err);
              }
            }
          }
          stopRinging();
          return;
        }

        await client.subscribe(user, mediaType);
        stopRinging();
      });

      localTracksRef.current = await AgoraRTC.createMicrophoneAndCameraTracks(
        selectedMicRef.current || undefined,
        selectedCameraRef.current || undefined
      );

      const [audioTrack, videoTrack] = localTracksRef.current;

      console.log('Created local tracks:', {
        audioTrack: !!audioTrack,
        videoTrack: !!videoTrack,
        audioMuted: audioTrack?.muted,
        audioEnabled: audioTrack?.enabled,
      });

      if (audioTrack) {
        try {
          await audioTrack.setMuted(false);
        } catch (err) {
          console.warn('Failed to unmute agent microphone track:', err);
        }
      }

      const playLocal = () => {
        const videoTrack = localTracksRef.current?.[1];
        if (videoTrack && localVideoRef.current) {
          try {
            console.log('Playing local video inside startCall:', localVideoRef.current);
            guardAgoraPlayResult(videoTrack.play(localVideoRef.current), 'local-startCall');
          } catch (err) {
            console.warn('Failed to play local video inside startCall:', err);
          }
        } else {
          console.warn('playLocal skipped', {
            videoTrack: !!videoTrack,
            localVideoRef: !!localVideoRef.current,
          });
        }
      };
      playLocal();

      let appId = agoraAppIdRef.current;
      if (!appId) {
        try {
          appId = await fetchAgoraAppId();
        } catch (err) {
          console.error('Failed to resolve Agora App ID:', err);
          alert(
            'Cannot start call: Agora App ID is not available from the server. '
            + 'Check Backend URL and server AGORA_APP_ID configuration.',
          );
          return;
        }
      }

      const joinedUid = await client.join(appId, roomId, token, agoraUid);

      if (primaryRemoteUidRef.current == null) {
        const others = client.remoteUsers.filter((u) => String(u.uid) !== String(joinedUid));
        if (others.length === 1) {
          primaryRemoteUidRef.current = others[0].uid;
          console.log('[agora] primaryRemoteUid inferred from remoteUsers', others[0].uid);
        }
      }

      client.on('user-joined', (user) => {
        const isCurrentSession = callSessionRef.current === sessionId && clientRef.current === client;
        if (!isCurrentSession) return;
        if (String(user.uid) === String(joinedUid)) return;
        if (primaryRemoteUidRef.current == null) {
          primaryRemoteUidRef.current = user.uid;
          console.log('[agora] primaryRemoteUid inferred from user-joined', user.uid);
        }
      });

      await client.publish(localTracksRef.current);

      console.log('Published local tracks:', {
        audioTrack: !!localTracksRef.current?.[0],
        videoTrack: !!localTracksRef.current?.[1],
      });

      playLocal();

      setPendingCall(null);
    } catch (err) {
      console.error('startCall failed:', err);
      stopRinging();
      setInCall(false);
      setCurrentRoomId(null);
      setRemoteUserId(null);
      setPendingCall(null);
      alert(`Call setup failed. Please check browser camera/microphone permission and make sure no other app is using them.\n\n${err?.message || err}`);
    } finally {
      setIsConnecting(false);
    }
  };

  const endCall = async (notifyServer = true, { clearPending = true } = {}) => {
    callSessionRef.current += 1;
    setAgoraNetworkWarn(false);
    stopRinging();
    const roomId = currentRoomId;
    try {
      try {
        remoteScreenSlotRef.current.track?.stop();
      } catch (_) {}
      remoteScreenSlotRef.current = { track: null, uid: null };
      try {
        remoteCameraSlotRef.current.track?.stop();
      } catch (_) {}
      remoteCameraSlotRef.current = { track: null, uid: null };
      primaryRemoteUidRef.current = null;
      remoteVideoPublishCountRef.current = {};
      setScreenShareActive(false);
      setScreenShareLayoutEpoch(0);
      if (localTracksRef.current) {
        localTracksRef.current.forEach(track => track.close());
        localTracksRef.current = null;
      }
      if (clientRef.current) {
        const client = clientRef.current;
        clientRef.current = null;
        await leaveClientSafely(client);
      }
      setInCall(false);
      setRemoteUserId(null);
      setCurrentRoomId(null);
      if (clearPending) {
        setPendingCall(null);
      }
      if (notifyServer && roomId) {
        socketRef.current?.emit('call_end', { roomId });
      }
    } finally {
      showNextPendingOfferRef.current();
    }
  };

  const handleAcceptIncoming = () => {
    console.log('[ui] accept clicked', pendingCall);
    if (!pendingCall || isConnecting) return;
    stopRinging();
    console.log('[socket] emitting call_accept', {
      roomId: pendingCall?.roomId,
    });
    socketRef.current?.emit('call_accept', { roomId: pendingCall.roomId });
    setPendingCall(null);
  };

  const handleRejectIncoming = () => {
    if (!pendingCall || isConnecting) return;
    const rejectRoomId = pendingCall.roomId;
    socketRef.current?.emit('call_reject', { roomId: rejectRoomId });
    stopRinging();
    setPendingCall(null);
    socketRef.current?.emit('agent_status', { agentId, status: 'idle' });
    window.setTimeout(() => showNextPendingOfferRef.current(), 100);
  };

  const handleAcceptNext = () => {
    startRinging('outgoing');
    socketRef.current?.emit('agent_status', { agentId, status: 'idle' });
  };

  const connectionDotColor =
    connectionStatus === 'connected' ? '#10b981'
      : connectionStatus === 'connecting' ? '#eab308'
        : '#ef4444';

  const connectionLabel =
    connectionStatus === 'connected' ? 'Online'
      : connectionStatus === 'connecting' ? 'Connecting'
        : connectionStatus === 'error' ? 'Conn. error' : 'Offline';

  const kioskStatusLabel = inCall ? 'In call' : pendingCall ? 'Incoming' : 'Idle';

  const hasActiveScreenShare = screenShareActive && !!remoteScreenSlotRef.current?.track;

  return (
    <>
      <style>
        {`
          @keyframes hqIncomingPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.35), 0 20px 50px rgba(15, 23, 42, 0.12); }
            50% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0.12), 0 24px 56px rgba(15, 23, 42, 0.14); }
          }
          .hq-take-next:not(:disabled):hover {
            transform: translateY(-1px);
          }
          .hq-side-nav-btn:focus,
          .hq-side-nav-btn:active {
            outline: none !important;
          }
          .hq-side-nav-btn:not(:focus-visible) {
            outline: none !important;
          }
          .agent-dashboard-no-share-remote-main video,
          .agent-dashboard-no-share-remote-main canvas {
            object-fit: cover;
            width: 100%;
            height: 100%;
          }
          .agent-dashboard-local-pip video,
          .agent-dashboard-local-pip canvas {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            background: #000 !important;
          }
          .agent-dashboard-guest-screen video,
          .agent-dashboard-guest-screen canvas {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            object-fit: contain !important;
            background: #000 !important;
            display: block !important;
          }
          .hq-language-pill:hover {
            transform: translateY(-1px);
            border-color: rgba(255, 255, 255, 0.45);
          }
        `}
      </style>
      <div style={styles.container} data-agent-default-ui-lang={agentDefaultUiLang}>
        <div style={styles.appShell}>
          <header style={styles.topBar}>
            <div style={styles.topBarLeft}>
              <img src="/logo192.png" alt="" style={styles.topLogo} />
              <div>
                <div style={styles.topTitle}>HQ Agent Console</div>
                <div style={styles.topSubtitle}>Hotel Kiosk Support Center</div>
              </div>
            </div>

            <div style={styles.headerLanguagesSection}>
              <div style={styles.headerLanguagesTitle}>
                <span aria-hidden>🌐</span>
                <span>Supported Languages</span>
              </div>
              <div style={styles.headerLanguagePills}>
                {SUPPORTED_LANGUAGES.map(({ code, label }) => {
                  const active = supportedLanguages.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      className="hq-language-pill"
                      onClick={() => handleSupportedLanguageToggle(code)}
                      aria-pressed={active}
                      style={{
                        ...styles.languagePill,
                        ...(active ? styles.languagePillActive : {}),
                      }}
                    >
                      {active ? `✓ ${label}` : label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={styles.topBarRight}>
              <div style={styles.connBadge}>
                <span style={{ ...styles.connDot, backgroundColor: connectionDotColor }} />
                <span>{connectionLabel}</span>
              </div>
              <div
                style={{
                  ...styles.agentIdPill,
                  maxWidth: 'min(48vw, 380px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={agentId}
              >
                Agent {agentId}
              </div>
            </div>
          </header>

          <div style={styles.body}>
            <aside style={styles.sidebar}>
              <div style={styles.sideNavLabel}>Workspace</div>
              <div style={styles.sideNavStatic}>
                <span style={styles.sideNavIcon}>◆</span> Overview
              </div>
              <button
                type="button"
                className="hq-side-nav-btn"
                onClick={() => setActiveView('queue')}
                style={{
                  ...styles.sideNavItem,
                  ...(activeView === 'queue' ? styles.sideNavItemActive : {}),
                }}
                aria-current={activeView === 'queue' ? 'page' : undefined}
              >
                <span style={styles.sideNavIcon}>◇</span> Queue
              </button>
              <button
                type="button"
                className="hq-side-nav-btn"
                onClick={() => setActiveView('history')}
                style={{
                  ...styles.sideNavItem,
                  ...(activeView === 'history' ? styles.sideNavItemActive : {}),
                }}
              >
                <span style={styles.sideNavIcon}>▤</span> Call History
              </button>
              <button
                type="button"
                className="hq-side-nav-btn"
                onClick={() => setActiveView('devices')}
                style={{
                  ...styles.sideNavItem,
                  ...(activeView === 'devices' ? styles.sideNavItemActive : {}),
                }}
              >
                <span style={styles.sideNavIcon}>◎</span> Devices
              </button>
              <button
                type="button"
                className="hq-side-nav-btn"
                onClick={() => setActiveView('settings')}
                style={{
                  ...styles.sideNavItem,
                  ...(activeView === 'settings' ? styles.sideNavItemActive : {}),
                }}
              >
                <span style={styles.sideNavIcon}>⚙</span> Settings
              </button>

              <div style={styles.sidebarCard}>
                <div style={styles.sidebarCardTitle}>Queue</div>
                <div style={styles.sidebarQueueNum}>{queueLength}</div>
                <div style={styles.sidebarCardHint}>guests waiting</div>
              </div>

              <div style={styles.sidebarCard}>
                <div style={styles.sidebarCardTitle}>Status</div>
                <span
                  style={{
                    ...styles.statusBadge,
                    ...(inCall ? styles.statusInCall : pendingCall ? styles.statusIncoming : styles.statusIdle),
                  }}
                >
                  {kioskStatusLabel}
                </span>
              </div>

              <div style={styles.sidebarCard}>
                <div style={styles.sidebarCardTitle}>Ringtone</div>
                <span style={{ ...styles.statusBadge, ...(ringtoneMuted ? styles.rtMuted : styles.rtOn) }}>
                  {ringtoneMuted ? 'Muted' : 'Enabled'}
                </span>
              </div>

              <div style={styles.sidebarFooter}>
                Powered by Agora RTC · Hotel Kiosk
              </div>
            </aside>

            <main
              style={{
                ...styles.content,
                ...(inCall ? styles.contentWhenInCall : {}),
              }}
            >
              {!inCall && !pendingCall && (
                <div style={styles.idleHeroCard}>
                  <div style={styles.idleEyebrow}>Standby</div>
                  <h2 style={styles.idleTitle}>Ready for next guest</h2>
                  <p style={styles.idleSubtitle}>Waiting for kiosk requests</p>
                  <div style={styles.idleQueueBig}>{queueLength}</div>
                  <p style={styles.idleQueueCaption}>Queue length</p>
                  <button
                    type="button"
                    className="hq-take-next"
                    onClick={handleAcceptNext}
                    disabled={queueLength === 0}
                    style={queueLength === 0 ? styles.takeNextDisabled : styles.takeNextEnabled}
                  >
                    Take next call
                  </button>
                  <p style={styles.idleHint}>Click when a guest is waiting</p>
                </div>
              )}

              {!inCall && pendingCall && (
                <div style={styles.incomingCard}>
                  <div style={styles.incomingBadge}>Incoming</div>
                  <h2 style={styles.incomingTitle}>Incoming guest request</h2>
                  <div style={styles.incomingMeta}>
                    <div>
                      <span style={styles.metaLabel}>Customer / Kiosk ID</span>
                      <div style={styles.metaValue}>{pendingCall.userId}</div>
                    </div>
                    <div>
                      <span style={styles.metaLabel}>Room ID</span>
                      <div style={styles.metaValueMono}>{pendingCall.roomId}</div>
                    </div>
                  </div>
                  <div style={styles.incomingActions}>
                    <button
                      type="button"
                      onClick={handleAcceptIncoming}
                      disabled={isConnecting}
                      style={{
                        ...styles.btnAccept,
                        ...(isConnecting ? { opacity: 0.55, cursor: 'not-allowed', boxShadow: 'none' } : {}),
                      }}
                    >
                      {isConnecting ? 'Connecting…' : 'Accept call'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRejectIncoming}
                      disabled={isConnecting}
                      style={{
                        ...styles.btnReject,
                        ...(isConnecting ? { opacity: 0.55, cursor: 'not-allowed' } : {}),
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {inCall && (
                <div
                  style={{
                    ...styles.callStage,
                    ...styles.callStageInCall,
                    ...(hasActiveScreenShare ? styles.callStageWhenScreenSharing : {}),
                  }}
                >
                  <div style={styles.callMeta}>
                    <div>
                      <div style={styles.callMetaLabel}>{tr('en', 'inCallWith')}</div>
                      <div style={styles.callMetaStrong}>{remoteUserId}</div>
                    </div>
                    <div style={styles.callMetaRoom}>
                      <span style={styles.callMetaLabel}>{tr('en', 'room')} </span>
                      <span style={styles.callMetaMono}>{currentRoomId}</span>
                    </div>
                  </div>

                  {(agoraNetworkWarn || connectionStatus === 'disconnected' || connectionStatus === 'error') && (
                    <div style={styles.networkBanner}>
                      {tr('en', 'networkUnstable')}
                    </div>
                  )}

                  <div style={styles.callVideoStageRoot}>
                    <div
                      style={{
                        ...styles.callVideoRow,
                        ...(hasActiveScreenShare ? styles.callVideoRowWhenSharing : styles.callVideoRowWhenNotSharing),
                      }}
                    >
                      {hasActiveScreenShare ? (
                        <div
                          style={{
                            ...styles.screenShareColumn,
                            ...styles.screenShareColumnVisible,
                            ...styles.screenShareColumnWhenSharing,
                          }}
                        >
                          <div style={styles.screenShareHeaderRow}>
                            <span style={styles.screenShareTitleText}>{tr('en', 'guestScreenShare')}</span>
                            <div style={styles.screenShareToolbar}>
                              <button
                                type="button"
                                style={{
                                  ...styles.screenShareZoomBtn,
                                  ...(screenShareScale >= 2 ? styles.screenShareZoomBtnDisabled : {}),
                                }}
                                disabled={screenShareScale >= 2}
                                onClick={handleScreenShareZoomIn}
                              >
                                {tr('en', 'zoomIn')}
                              </button>
                              <button
                                type="button"
                                style={{
                                  ...styles.screenShareZoomBtn,
                                  ...(screenShareScale <= 1 ? styles.screenShareZoomBtnDisabled : {}),
                                }}
                                disabled={screenShareScale <= 1}
                                onClick={handleScreenShareZoomOut}
                              >
                                {tr('en', 'zoomOut')}
                              </button>
                              <button
                                type="button"
                                style={styles.screenShareZoomBtn}
                                onClick={handleScreenShareZoomReset}
                              >
                                {tr('en', 'zoomReset')}
                              </button>
                            </div>
                          </div>
                          <div
                            className="agent-dashboard-guest-screen"
                            style={{
                              ...styles.screenShareScrollHost,
                              ...styles.screenShareScrollHostWhenSharing,
                            }}
                          >
                            <div
                              role="presentation"
                              onPointerDown={handleScreenShareViewportPointerDown}
                              onPointerMove={handleScreenShareViewportPointerMove}
                              onPointerUp={handleScreenShareViewportPointerUp}
                              onPointerCancel={handleScreenShareViewportPointerUp}
                              style={{
                                ...styles.screenShareZoomViewport,
                                touchAction: screenShareScale > 1 ? 'none' : 'auto',
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transform: `translate(${screenShareOffsetX}px, ${screenShareOffsetY}px) scale(${screenShareScale})`,
                                  transformOrigin: 'center center',
                                  cursor:
                                    screenShareScale > 1
                                      ? (isDraggingScreenShare ? 'grabbing' : 'grab')
                                      : 'default',
                                  willChange: isDraggingScreenShare ? 'transform' : undefined,
                                }}
                              >
                                <div
                                  ref={screenShareVideoRef}
                                  style={{
                                    ...styles.screenShareVideo,
                                    ...styles.screenShareVideoWhenSharing,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            ...styles.remoteMainColumn,
                            ...styles.remoteMainColumnWhenNotSharing,
                          }}
                        >
                          <div style={styles.callGuestVideoStage}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flex: 1,
                                minHeight: 0,
                                width: '100%',
                                overflow: 'hidden',
                                ...styles.guestVideoMainHost,
                              }}
                            >
                              <div style={styles.localLabel}>
                                {tr('en', 'guestMainLabel')}
                              </div>
                              <div
                                ref={remoteVideoRef}
                                className="agent-dashboard-no-share-remote-main"
                                style={styles.guestMainVideo}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={styles.floatingPipStack}>
                      <div className="agent-dashboard-local-pip" style={styles.localPipFloating}>
                        <div style={styles.localLabel}>{tr('en', 'you')}</div>
                        <div ref={localVideoRef} style={styles.localVideo} />
                      </div>
                      <button
                        type="button"
                        onClick={() => { endCall(true); }}
                        style={styles.endCallFloating}
                      >
                        {tr('en', 'endCall')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </main>

            <aside
              style={{
                ...styles.rightPanel,
                ...styles.rightPanelMargins,
              }}
            >
                {rightPanelView === 'history' && (
                  <div style={styles.rpSection}>
                    <h3 style={styles.rpTitle}>{tr('en', 'callHistory')}</h3>
                    {historySource === 'recent_fallback' && history.length > 0 && (
                      <div style={styles.historyRecentBanner}>{tr('en', 'historyRecentAllHint')}</div>
                    )}
                    {historyLoadError ? (
                      <div style={styles.historyErrorBox}>{historyLoadError}</div>
                    ) : history.length === 0 ? (
                      <div style={styles.emptyState}>
                        <div>{tr('en', 'noCallRecords')}</div>
                        <p style={styles.historyEmptyHint}>{tr('en', 'historyEmptyHint')}</p>
                        <p style={styles.historyAgentIdHint}>
                          <span style={styles.historyLabel}>agent id</span>
                          {' '}
                          <span style={styles.callMetaMono}>{agentId}</span>
                        </p>
                      </div>
                    ) : (
                      <div style={styles.historyCardList}>
                        {history.map((item, idx) => (
                          <div key={idx} style={styles.historyCard}>
                            <div style={styles.historyCardRow}>
                              <span style={styles.historyLabel}>{tr('en', 'clientKiosk')}</span>
                              <span style={styles.historyVal}>{item.device_id}</span>
                            </div>
                            <div style={styles.historyCardRow}>
                              <span style={styles.historyLabel}>{tr('en', 'time')}</span>
                              <span style={styles.historyVal}>{new Date(item.start_time).toLocaleString()}</span>
                            </div>
                            <div style={styles.historyCardRow}>
                              <span style={styles.historyLabel}>{tr('en', 'duration')}</span>
                              <span style={styles.historyVal}>
                                {item.duration == null
                                  ? tr('en', 'inProgress')
                                  : `${Math.floor(item.duration / 60)}m ${item.duration % 60}s`}
                              </span>
                            </div>
                            {item.room_id != null && item.room_id !== '' && (
                              <div style={styles.historyRoomId}>room_id: {item.room_id}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {rightPanelView === 'devices' && (
                  <div style={styles.rpSection}>
                    <h3 style={styles.rpTitle}>{tr('en', 'cameraMic')}</h3>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{tr('en', 'camera')}</label>
                      <select
                        value={selectedCamera}
                        onChange={(e) => setSelectedCamera(e.target.value)}
                        style={styles.selectFull}
                      >
                        {cameras.map((cam) => (
                          <option key={cam.deviceId} value={cam.deviceId}>
                            {cam.label || `Camera ${cam.deviceId.slice(0, 5)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{tr('en', 'microphone')}</label>
                      <select
                        value={selectedMic}
                        onChange={(e) => setSelectedMic(e.target.value)}
                        style={styles.selectFull}
                      >
                        {microphones.map((mic) => (
                          <option key={mic.deviceId} value={mic.deviceId}>
                            {mic.label || `Mic ${mic.deviceId.slice(0, 5)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.volCard}>
                      <div style={styles.volCardTitle}>{tr('en', 'inputLevel')}</div>
                      <div style={styles.volumeBarBg}>
                        <div style={{ ...styles.volumeBarFill, width: `${Math.min(100, micVolume)}%` }} />
                      </div>
                      <div style={styles.volumeValue}>{Math.round(micVolume)}%</div>
                    </div>
                    <video ref={previewVideoRef} autoPlay muted playsInline style={styles.previewVideoModern} />
                    {previewError && <div style={styles.errorMsg}>{previewError}</div>}
                    <div style={styles.deviceBtnRow}>
                      <button type="button" onClick={startPreview} style={styles.btnNeutral}>{tr('en', 'test')}</button>
                      <button type="button" onClick={stopPreview} style={styles.btnNeutral}>{tr('en', 'stop')}</button>
                      <button type="button" onClick={saveDeviceSettings} style={styles.btnPrimarySm}>{tr('en', 'saveAndClose')}</button>
                      <button type="button" onClick={() => setActiveView('queue')} style={styles.btnNeutral}>{tr('en', 'close')}</button>
                    </div>
                  </div>
                )}

                {rightPanelView === 'settings' && (
                  <div style={styles.rpSection}>
                    <h3 style={styles.rpTitle}>{tr('en', 'settings')}</h3>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{tr('en', 'backendUrl')}</label>
                      <input
                        value={editingBackendUrl}
                        onChange={(e) => setEditingBackendUrl(e.target.value)}
                        placeholder="https://hotelkiosk.top"
                        style={styles.inputFull}
                      />
                    </div>
                    <div style={styles.deviceBtnRow}>
                      <button type="button" onClick={saveBackendUrl} style={styles.btnPrimarySm}>{tr('en', 'save')}</button>
                      <button type="button" onClick={() => setActiveView('queue')} style={styles.btnNeutral}>{tr('en', 'close')}</button>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{tr('en', 'ringtone')}</label>
                      <button
                        type="button"
                        onClick={() => setRingtoneMuted((prev) => !prev)}
                        style={styles.btnNeutral}
                      >
                        {ringtoneMuted ? tr('en', 'muted') : tr('en', 'enabled')}
                      </button>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Ringtone volume: {ringtoneVolume}%</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={ringtoneVolume}
                        onChange={(e) => setRingtoneVolume(Number(e.target.value))}
                        style={styles.rangeFull}
                      />
                    </div>
                    {isRinging && <p style={styles.hint}>Ringing… stops when the call connects or ends.</p>}
                    <p style={styles.hint}>Current: {backendUrl}</p>
                  </div>
                )}
              </aside>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    height: '100vh',
    overflow: 'hidden',
    fontFamily: 'Inter, Segoe UI, Roboto, sans-serif',
    backgroundColor: '#f8fafc',
    color: '#0f172a',
  },
  appShell: {
    minHeight: '100vh',
    height: '100vh',
    maxHeight: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    minHeight: 72,
    padding: '12px 28px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    rowGap: 10,
    flexShrink: 0,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #1d4ed8 100%)',
    color: '#fff',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.25)',
    overflow: 'hidden',
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexShrink: 0,
  },
  headerLanguagesSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 360,
    maxWidth: 720,
    flex: 1,
    marginLeft: 8,
    marginRight: 8,
  },
  headerLanguagesTitle: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: 'rgba(255,255,255,0.72)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
  },
  headerLanguagePills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  languagePill: {
    border: '1px solid rgba(255,255,255,0.24)',
    background: 'rgba(255,255,255,0.08)',
    color: '#E5E7EB',
    borderRadius: 999,
    padding: '7px 12px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 160ms ease',
    lineHeight: 1.2,
    fontFamily: 'inherit',
  },
  languagePillActive: {
    background: 'linear-gradient(135deg, #2563eb, #22c55e)',
    borderColor: 'rgba(255,255,255,0.42)',
    color: '#FFFFFF',
    boxShadow: '0 8px 22px rgba(37,99,235,0.25)',
  },
  topLogo: {
    width: 44,
    height: 44,
    borderRadius: 12,
    objectFit: 'cover',
    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
  },
  topTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  topSubtitle: {
    fontSize: '0.78rem',
    opacity: 0.88,
    marginTop: 2,
    color: 'rgba(255,255,255,0.85)',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  connBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.18)',
    fontSize: '0.82rem',
    fontWeight: 800,
  },
  connDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  agentIdPill: {
    padding: '9px 16px',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.16)',
    fontSize: '0.8rem',
    fontWeight: 800,
  },
  body: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
  },
  sidebar: {
    width: 138,
    flexShrink: 0,
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    padding: '18px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    boxShadow: '4px 0 24px rgba(15, 23, 42, 0.06)',
  },
  sideNavLabel: {
    fontSize: '0.6rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#64748b',
    marginBottom: 8,
    paddingLeft: 4,
  },
  sideNavStatic: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 10px',
    borderRadius: 10,
    color: '#64748b',
    fontSize: '0.76rem',
    fontWeight: 600,
    background: 'transparent',
    opacity: 0.85,
    lineHeight: 1.25,
  },
  sideNavItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 10px',
    borderRadius: 10,
    border: '1px solid transparent',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: '#cbd5e1',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.18s ease',
    outline: 'none',
    boxShadow: 'none',
    appearance: 'none',
  },
  sideNavItemActive: {
    backgroundColor: '#ffffff',
    color: '#0f172a',
    border: '1px solid rgba(226, 232, 240, 0.9)',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
  },
  sideNavIcon: {
    opacity: 0.85,
    fontSize: '0.86rem',
  },
  sidebarCard: {
    marginTop: 10,
    padding: '10px 8px',
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  sidebarCardTitle: {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#94a3b8',
    marginBottom: 8,
  },
  sidebarQueueNum: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#fff',
    lineHeight: 1,
  },
  sidebarCardHint: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: 4,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: '0.78rem',
    fontWeight: 700,
  },
  statusIdle: {
    backgroundColor: 'rgba(100, 116, 139, 0.35)',
    color: '#e2e8f0',
  },
  statusIncoming: {
    backgroundColor: 'rgba(37, 99, 235, 0.35)',
    color: '#bfdbfe',
  },
  statusInCall: {
    backgroundColor: 'rgba(16, 185, 129, 0.28)',
    color: '#a7f3d0',
  },
  rtMuted: {
    backgroundColor: 'rgba(239, 68, 68, 0.22)',
    color: '#fecaca',
  },
  rtOn: {
    backgroundColor: 'rgba(16, 185, 129, 0.22)',
    color: '#a7f3d0',
  },
  sidebarFooter: {
    marginTop: 'auto',
    paddingTop: 20,
    fontSize: '0.65rem',
    color: '#475569',
    lineHeight: 1.45,
    paddingLeft: 4,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
  },
  /** In-call: main column stretches so the video row can use remaining height. */
  contentWhenInCall: {
    flex: 1,
    minHeight: 0,
    padding: '14px 14px 24px',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  rightPanel: {
    width: 352,
    minWidth: 352,
    maxWidth: 352,
    flexShrink: 0,
    backgroundColor: '#fff',
    borderLeft: '1px solid #e2e8f0',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: '-12px 0 40px rgba(15, 23, 42, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    alignSelf: 'stretch',
  },
  rightPanelMargins: {
    marginLeft: 14,
    marginTop: 14,
  },
  rpSection: {
    padding: 22,
    borderBottom: '1px solid #f1f5f9',
  },
  rpTitle: {
    margin: '0 0 16px',
    fontSize: '1rem',
    fontWeight: 800,
    color: '#0f172a',
  },
  emptyState: {
    padding: '28px 16px',
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.9rem',
    borderRadius: 16,
    border: '1px dashed #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  historyErrorBox: {
    padding: '16px 14px',
    borderRadius: 14,
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    fontSize: '0.85rem',
    fontWeight: 600,
    lineHeight: 1.45,
  },
  historyRecentBanner: {
    marginBottom: 14,
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#92400e',
    fontSize: '0.78rem',
    fontWeight: 600,
    lineHeight: 1.45,
  },
  historyEmptyHint: {
    margin: '14px 0 0',
    fontSize: '0.78rem',
    color: '#94a3b8',
    lineHeight: 1.5,
    textAlign: 'center',
  },
  historyAgentIdHint: {
    margin: '12px 0 0',
    fontSize: '0.78rem',
    color: '#64748b',
  },
  historyCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  historyCard: {
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    padding: '14px 16px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
  },
  historyCardRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginBottom: 8,
  },
  historyLabel: {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#64748b',
    fontWeight: 700,
  },
  historyVal: {
    fontSize: '0.85rem',
    color: '#0f172a',
    fontWeight: 600,
  },
  historyRoomId: {
    fontSize: '0.72rem',
    color: '#94a3b8',
    marginTop: 4,
    fontFamily: 'ui-monospace, monospace',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 14,
  },
  label: {
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#64748b',
  },
  selectFull: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    fontSize: '0.88rem',
    backgroundColor: '#f8fafc',
    fontWeight: 600,
  },
  inputFull: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    fontSize: '0.88rem',
    boxSizing: 'border-box',
  },
  rangeFull: {
    width: '100%',
  },
  volCard: {
    padding: 14,
    borderRadius: 16,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: 14,
  },
  volCardTitle: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#64748b',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  volumeBarBg: {
    backgroundColor: '#e2e8f0',
    height: 10,
    borderRadius: 999,
    overflow: 'hidden',
  },
  volumeBarFill: {
    background: 'linear-gradient(90deg, #10b981, #34d399)',
    height: '100%',
    borderRadius: 999,
    transition: 'width 0.12s ease',
  },
  volumeValue: {
    fontSize: '0.75rem',
    textAlign: 'right',
    marginTop: 6,
    color: '#64748b',
    fontWeight: 700,
  },
  previewVideoModern: {
    width: '100%',
    backgroundColor: '#0f172a',
    borderRadius: 16,
    aspectRatio: '16 / 9',
    objectFit: 'cover',
    marginBottom: 12,
  },
  deviceBtnRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
  },
  btnNeutral: {
    border: 'none',
    borderRadius: 999,
    padding: '12px 18px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    backgroundColor: '#f1f5f9',
    color: '#0f172a',
    fontSize: '0.82rem',
  },
  btnPrimarySm: {
    border: 'none',
    borderRadius: 999,
    padding: '12px 18px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    fontSize: '0.82rem',
    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.25)',
  },
  errorMsg: {
    color: '#ef4444',
    fontSize: '0.8rem',
    marginBottom: 8,
  },
  hint: {
    fontSize: '0.82rem',
    color: '#64748b',
    marginTop: 8,
    lineHeight: 1.45,
  },
  idleHeroCard: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 24,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    padding: '44px 40px',
    textAlign: 'center',
  },
  idleEyebrow: {
    fontSize: '0.7rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: '#2563eb',
    marginBottom: 12,
  },
  idleTitle: {
    margin: '0 0 8px',
    fontSize: '1.75rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#0f172a',
  },
  idleSubtitle: {
    margin: '0 0 28px',
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: 500,
  },
  idleQueueBig: {
    fontSize: '4rem',
    fontWeight: 800,
    lineHeight: 1,
    color: '#0f172a',
    letterSpacing: '-0.04em',
  },
  idleQueueCaption: {
    margin: '8px 0 28px',
    fontSize: '0.85rem',
    color: '#94a3b8',
    fontWeight: 600,
  },
  takeNextEnabled: {
    border: 'none',
    borderRadius: 999,
    padding: '16px 36px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    fontSize: '1.05rem',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    boxShadow: '0 16px 40px rgba(16, 185, 129, 0.28)',
  },
  takeNextDisabled: {
    border: 'none',
    borderRadius: 999,
    padding: '16px 36px',
    fontWeight: 700,
    cursor: 'not-allowed',
    transition: 'all 0.18s ease',
    fontSize: '1.05rem',
    backgroundColor: '#cbd5e1',
    color: '#f1f5f9',
  },
  idleHint: {
    marginTop: 18,
    fontSize: '0.85rem',
    color: '#64748b',
  },
  incomingCard: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 24,
    padding: '40px 36px',
    backgroundColor: '#fff',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(135deg, #2563eb, #10b981)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    animation: 'hqIncomingPulse 2.4s ease-in-out infinite',
  },
  incomingBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 999,
    backgroundColor: 'rgba(37, 99, 235, 0.12)',
    color: '#1d4ed8',
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 16,
  },
  incomingTitle: {
    margin: '0 0 24px',
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  incomingMeta: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginBottom: 32,
    textAlign: 'left',
  },
  metaLabel: {
    display: 'block',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#64748b',
    fontWeight: 700,
    marginBottom: 6,
  },
  metaValue: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#0f172a',
    wordBreak: 'break-all',
  },
  metaValueMono: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#0f172a',
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    wordBreak: 'break-all',
  },
  incomingActions: {
    display: 'flex',
    gap: 14,
    flexWrap: 'wrap',
  },
  btnAccept: {
    flex: '1 1 200px',
    border: 'none',
    borderRadius: 999,
    padding: '18px 24px',
    fontWeight: 800,
    fontSize: '1.05rem',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    boxShadow: '0 12px 32px rgba(16, 185, 129, 0.3)',
  },
  btnReject: {
    flex: '1 1 160px',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    padding: '18px 24px',
    fontWeight: 800,
    fontSize: '1.05rem',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    backgroundColor: '#f1f5f9',
    color: '#475569',
  },
  callStage: {
    width: '100%',
    maxWidth: 1100,
    borderRadius: 24,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    padding: 24,
  },
  callStageInCall: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  /** Let call card span main column when sharing (flex row uses full width) */
  callStageWhenScreenSharing: {
    maxWidth: '100%',
    width: '100%',
  },
  callMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 14,
  },
  callMetaLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#64748b',
    fontWeight: 700,
  },
  callMetaStrong: {
    fontSize: '1.15rem',
    fontWeight: 800,
    color: '#0f172a',
    marginTop: 4,
  },
  callMetaRoom: {
    textAlign: 'right',
  },
  callMetaMono: {
    fontFamily: 'ui-monospace, monospace',
    fontWeight: 700,
    color: '#0f172a',
  },
  networkBanner: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '12px 16px',
    borderRadius: 14,
    marginBottom: 14,
    fontSize: '0.88rem',
    fontWeight: 600,
    textAlign: 'center',
    border: '1px solid #fde68a',
  },
  callVideoStageRoot: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  callVideoRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 14,
    alignItems: 'stretch',
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  /** Guest share + camera column share one row; no page scroll inside */
  callVideoRowWhenSharing: {
    alignItems: 'stretch',
    minHeight: 0,
    gap: 0,
  },
  callVideoRowWhenNotSharing: {
    alignItems: 'stretch',
    minHeight: 0,
    gap: 0,
    width: '100%',
  },
  remoteMainColumn: {
    flex: '1 1 0%',
    minWidth: 0,
  },
  remoteMainColumnWhenNotSharing: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    overflow: 'hidden',
  },
  callGuestVideoStage: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  screenShareColumn: {
    flex: '0 1 400px',
    minWidth: 0,
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    overflow: 'visible',
    backgroundColor: '#0f172a',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
  },
  screenShareColumnVisible: {
    minHeight: 200,
  },
  screenShareColumnCollapsed: {
    flex: '0 0 0px',
    width: 0,
    minWidth: 0,
    maxWidth: 0,
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    visibility: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
    border: 'none',
    minHeight: 0,
    boxShadow: 'none',
  },
  /** Guest screen column uses full row width while sharing (remote column is collapsed to 0). */
  screenShareColumnWhenSharing: {
    flex: '1 1 0%',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    minHeight: 0,
    alignSelf: 'stretch',
  },
  screenShareHeaderRow: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 25,
    minHeight: 44,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    padding: '10px 12px',
    paddingRight: 200,
    backgroundColor: 'rgba(15, 23, 42, 0.96)',
    borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
  },
  screenShareTitleText: {
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#e2e8f0',
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.3,
  },
  screenShareToolbar: {
    position: 'absolute',
    top: 8,
    right: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    zIndex: 26,
  },
  screenShareZoomBtn: {
    border: '1px solid rgba(148, 163, 184, 0.45)',
    borderRadius: 8,
    padding: '6px 10px',
    fontSize: '0.72rem',
    fontWeight: 700,
    cursor: 'pointer',
    backgroundColor: 'rgba(30, 41, 59, 0.95)',
    color: '#f1f5f9',
    outline: 'none',
    whiteSpace: 'nowrap',
  },
  screenShareZoomBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  /** Fills column under label; when sharing, becomes fixed portrait viewport (no scroll — avoids play issues) */
  screenShareScrollHost: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#1e293b',
  },
  screenShareScrollHostWhenSharing: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  screenShareZoomViewport: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    borderRadius: '0 0 14px 14px',
  },
  screenShareVideo: {
    flex: '1 1 0%',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#1e293b',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Fills scroll host; PiP is position:absolute and does not reduce this width. */
  screenShareVideoWhenSharing: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    alignSelf: 'stretch',
    flex: '1 1 auto',
  },
  floatingPipStack: {
    position: 'absolute',
    right: 24,
    bottom: 24,
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
    pointerEvents: 'none',
  },
  localPipFloating: {
    position: 'relative',
    zIndex: 20,
    width: 180,
    height: 320,
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: '#000',
    boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
    border: '2px solid rgba(255,255,255,0.2)',
    pointerEvents: 'auto',
  },
  endCallFloating: {
    width: 180,
    height: 44,
    border: 'none',
    borderRadius: 12,
    padding: 0,
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    backgroundColor: '#ef4444',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)',
    transition: 'all 0.18s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    pointerEvents: 'auto',
  },
  /** Remote guest / kiosk camera main slot (fills stage; letterboxing via object-fit on video) */
  guestVideoMainHost: {
    position: 'relative',
    flex: '1 1 auto',
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: '#0f172a',
    boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.35)',
  },
  /** Remote guest track in main slot — kiosk camera fills area (cover) */
  guestMainVideo: {
    width: '100%',
    height: '100%',
    backgroundColor: '#0f172a',
    objectFit: 'cover',
  },
  remoteVideo: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
  },
  localLabel: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 2,
    padding: '4px 10px',
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  localVideo: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
    objectFit: 'cover',
  },
};

export default AgentDashboard;